/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { makeStateKey } from '@angular/core';
import { RouterReducerState } from '@ngrx/router-store';

import { IManagePostedSpeedState } from '../../../manage-posted-speed/store/manage-posted-speed.state';
import { IReplayState } from '../../../replay/store';
import { ISearchState } from '../../../search/store';
import { IAdvancedOptionsState, initialAdvancedOptionsState } from './advanced-options.state';
import { IAssetsState, initialAssetsState } from './assets.state';
import { IAttributeState, initialAttributeState } from './attribute.state';
import { IBalloonInfoState, initialBalloonInfoState } from './balloon-info.state';
import { ICustomerMetadataState, initialCustomerMetadataState } from './customer-metadata.state';
import { IDeviceHealthCheckState, initialDeviceHealthCheckState } from './device-health-check.state';
import { IDeviceHealthState, initialDeviceHealthState } from './device-health.state';
import { IDriverMessageState, initialDriverMessageState } from './driver-message.state';
import { IEcmPanelState, initialEcmPanelState } from './ecm-panel.state';
import { IGarminStopHistoryState, initialGarminStopHistoryState } from './garmin-stop-history.state';
import { IHarshDrivingEventsState, initialHarshDrivingEventsState } from './harsh-driving-events.state';
import { ILayoutPreferencesState, initialLayoutPreferencesState } from './layout-preferences.state';
import { ILayoutState, initialLayoutState } from './layout.state';
import { IMapOptionsState, initialMapOptionsState } from './map-options.state';
import { IMapsState, initialMapsState } from './maps.state';
import { IModulesState, initialModulesState } from './modules.state';
import { initialPlotsState, IPlotsState } from './plots.state';
import { initialReverseGeocodeState, IReverseGeocodeState } from './reverse-geocode.state';
import { initialRoadsideAssistanceState, IRoadsideAssistanceState } from './roadside-assistance.state';
import { IRouterStateUrl } from './router-state.serializer';
import { initialSendToGarminState, ISendToGarminState } from './send-to-garmin.state';
import { initialSidePanelState, ISidePanelState } from './side-panel.state';
import { initialUserDataState, IUserMapSettingsState } from './user-map-settings.state';
import { initialVehicleDriverPanelState, IVehicleDriverPanelState } from './vehicle-driver-panel.state';
import { initialVehicleListPanelState, IVehicleListPanelState } from './vehicle-list-panel.state';
import { initialVehicleState, IVehicleState } from './vehicle.state';
import { initialDeviceInfoState, IVideoDeviceInfoState } from './video-device-info.state';
import { initialWorkOrderState, IWorkOrderState } from './work-order.state';
import { initialWorkingTimeDirectiveState, IWorkingTimeDirectiveState } from './working-time-directive.state';

export const ROOT_STATE_KEY = makeStateKey('fm-app');

export interface IAppState {
  router?: RouterReducerState<IRouterStateUrl>;
  customerMetadata: ICustomerMetadataState;
  layout: ILayoutState;
  layoutPreferences: ILayoutPreferencesState;
  plots: IPlotsState;
  userMapSettings: IUserMapSettingsState;
  mapOptions: IMapOptionsState;
  vehicleDriverPanel: IVehicleDriverPanelState;
  advancedOptions: IAdvancedOptionsState;
  balloonInfo: IBalloonInfoState;
  vehicle: IVehicleState;
  maps: IMapsState;
  vehicleListPanel: IVehicleListPanelState;
  workingTimeDirective: IWorkingTimeDirectiveState;
  driverMessage: IDriverMessageState;
  garminStopHistory: IGarminStopHistoryState;
  sendToGarmin: ISendToGarminState;
  reverseGeocode: IReverseGeocodeState;
  workOrder: IWorkOrderState;
  roadsideAssistance: IRoadsideAssistanceState;
  assets: IAssetsState;
  attribute: IAttributeState;
  harshDrivingEvent: IHarshDrivingEventsState;
  modules: IModulesState;
  sidePanel: ISidePanelState;
  deviceHealthCheck: IDeviceHealthCheckState;
  ecmPanel: IEcmPanelState;
  videoDeviceInfo: IVideoDeviceInfoState;
  // Replay state is needed here to avoid ngrx warnings related to not being able to access a lazy loaded module feature state
  replay?: IReplayState;
  search?: ISearchState;
  deviceHealth?: IDeviceHealthState;
  managePostedSpeed?: IManagePostedSpeedState;
}

export const initialAppState: IAppState = {
  customerMetadata: initialCustomerMetadataState,
  layout: initialLayoutState,
  layoutPreferences: initialLayoutPreferencesState,
  plots: initialPlotsState,
  userMapSettings: initialUserDataState,
  mapOptions: initialMapOptionsState,
  vehicleDriverPanel: initialVehicleDriverPanelState,
  advancedOptions: initialAdvancedOptionsState,
  balloonInfo: initialBalloonInfoState,
  vehicle: initialVehicleState,
  maps: initialMapsState,
  vehicleListPanel: initialVehicleListPanelState,
  workingTimeDirective: initialWorkingTimeDirectiveState,
  driverMessage: initialDriverMessageState,
  garminStopHistory: initialGarminStopHistoryState,
  sendToGarmin: initialSendToGarminState,
  reverseGeocode: initialReverseGeocodeState,
  workOrder: initialWorkOrderState,
  roadsideAssistance: initialRoadsideAssistanceState,
  assets: initialAssetsState,
  attribute: initialAttributeState,
  harshDrivingEvent: initialHarshDrivingEventsState,
  modules: initialModulesState,
  sidePanel: initialSidePanelState,
  deviceHealthCheck: initialDeviceHealthCheckState,
  ecmPanel: initialEcmPanelState,
  videoDeviceInfo: initialDeviceInfoState,
  deviceHealth: initialDeviceHealthState
};
